import React, { useCallback, useState } from 'react';

import { IconButton, Menu, MenuItem, TableCell, TableRow, TextField, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { prettyDateTime } from '../../../lib/helpers/helpers';
import useCurrentUser from '../../../lib/hooks/useCurrentUser';
import { Check, MoreVert } from '@mui/icons-material';
import { deletePatientNote, updatePatientNote } from '../../../lib/api';
import { AdminButton, AdminProgressBar } from '../../../components';
import { useSnackbar } from 'notistack';
import { truncate } from '../../../lib/helpers/helpers';

const useStyles = makeStyles((theme) => ({
  tableRow: theme.globalClasses.adminTableRowWrapper,
  expandedRow: {
    '& td': {
      verticalAlign: 'top',
    },
  },
  textFieldContainer: {
    width: '100%',
    minHeight: 100,
  },
  editTextField: {
    width: '100%',
  },
  truncatedText: {
    cursor: 'default', // Shows the default cursor on hover to indicate tooltip
  },
}));

const PatientNotesRow = ({ row, refetch }) => {
  const classes = useStyles();
  const { userID } = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const moreMenu = Boolean(anchorEl);

  const truncateText = (text) => {
    if (!text || text === '') return 'N/A';
    return truncate(text, 70);
  };

  const handleMenu = useCallback(
    (event) => {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    },
    [anchorEl],
  );

  const handleEdit = useCallback(() => {
    if (!editMode) {
      setEditedContent(row.content || '');
    }
    setEditMode(!editMode);
    setAnchorEl(null);
  }, [editMode, row.content]);

  const deleteNote = useCallback(
    (noteID) => {
      const confirm = window.confirm('Confirm Note Deletion?');
      if (confirm) {
        deletePatientNote(noteID)
          .then(() => {
            setAnchorEl(null);
            refetch();
            enqueueSnackbar('Success', {
              variant: 'success',
              autoHideDuration: 3000,
            });
          })
          .catch((error) => {
            console.error('Error deleting note:', error);
            enqueueSnackbar('Error deleting note', {
              variant: 'error',
              autoHideDuration: 3000,
            });
          });
      }
    },
    [enqueueSnackbar],
  );

  const editNote = useCallback(
    (noteID) => {
      if (editedContent?.trim()) {
        setIsSaving(true);
        updatePatientNote(noteID, editedContent.trim())
          .then(() => {
            setAnchorEl(null);
            setIsSaving(false);
            setEditMode(false);
            refetch();
            enqueueSnackbar('Success', {
              variant: 'success',
              autoHideDuration: 3000,
            });
          })
          .catch((error) => {
            console.error('Error updating note:', error);
            setIsSaving(false);
            enqueueSnackbar('Error updating note', {
              variant: 'error',
              autoHideDuration: 3000,
            });
          });
      }
    },
    [editedContent, enqueueSnackbar],
  );

  const handleCancel = useCallback(() => {
    setEditMode(false);
    setEditedContent('');
  }, []);

  return (
    <>
      <TableRow
        tabIndex={-1}
        id={row.id}
        key={row.id}
        className={`${classes.tableRow} ${editMode ? classes.expandedRow : ''}`}
      >
        <TableCell align="left">{prettyDateTime(row.createdAt)}</TableCell>
        <TableCell align="left">{row.ownerName ?? 'N/A'}</TableCell>
        <TableCell align="left">
          {editMode ? (
            <div className={classes.textFieldContainer}>
              <TextField
                onChange={(event) => setEditedContent(event?.target?.value)}
                className={classes.editTextField}
                multiline={true}
                minRows={3}
                maxRows={20}
                fullWidth
                value={editedContent}
                variant="outlined"
              />
            </div>
          ) : (
            <Tooltip
              title={row.content || 'N/A'}
              placement="top-start"
              arrow
              enterDelay={500}
              leaveDelay={200}
            >
              <div className={classes.truncatedText}>{truncateText(row.content)}</div>
            </Tooltip>
          )}
        </TableCell>
        <TableCell align="left">{Boolean(row.is_assessment) && <Check />}</TableCell>
        <TableCell align="left">{Boolean(row.is_consultation) && <Check />}</TableCell>
        <TableCell align="left">
          {userID == row.owner ? (
            !editMode ? (
              <IconButton
                aria-label="more"
                aria-controls="long-menu"
                aria-haspopup="true"
                onClick={handleMenu}
                size="large"
              >
                <MoreVert />
              </IconButton>
            ) : (
              <>
                {isSaving ? (
                  <AdminProgressBar style={{ width: '100%', margin: '20px auto 20px' }} />
                ) : (
                  <AdminButton
                    fullWidth
                    style={{ marginBottom: 20 }}
                    onClick={() => editNote(row.id)}
                    disabled={!editedContent?.trim()}
                  >
                    Save
                  </AdminButton>
                )}
                <AdminButton
                  fullWidth
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </AdminButton>
              </>
            )
          ) : (
            ''
          )}
        </TableCell>
      </TableRow>
      <Menu
        id={'moreMenu_' + row.id}
        anchorEl={anchorEl}
        keepMounted
        open={moreMenu}
        onClose={handleMenu}
      >
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
        <MenuItem onClick={() => deleteNote(row.id)}>Delete</MenuItem>
      </Menu>
    </>
  );
};

export default PatientNotesRow;
